import { IonSelect, IonSelectOption } from "@ionic/react";
import { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { getErrorText } from "@/components/atoms/form-ion/utils";
import { useFieldContext } from "@/form/common/hook";
import type { AppFormFieldLabelProps } from "@/form/components/field-label";
import type { AppFormFieldProps } from "@/form/components/types";
import { cn } from "@/lib/style/cva.config";

export type SelectFieldProps = AppFormFieldLabelProps & {
  withAsterisk?: boolean;
  withPlaceholder?: boolean;
  options: Array<{ label: string; value: string }>;
  multiple?: boolean;
};

export const SelectField = forwardRef<
  HTMLIonSelectElement,
  React.ComponentProps<typeof IonSelect> & AppFormFieldProps & SelectFieldProps
>(function SelectField(
  { withAsterisk = true, withPlaceholder = false, options, multiple = false, ...props },
  ref,
) {
  const { t, i18n } = useTranslation();
  const field = useFieldContext<string | string[]>();

  return (
    <IonSelect
      ref={ref}
      labelPlacement={props.labelPlacement ?? "stacked"}
      label={t(`fields:${props.name ?? field.name}.label`)}
      placeholder={
        withPlaceholder ? t(`fields:${props.name ?? field.name}.placeholder`) : undefined
      }
      name={field.name}
      value={field.state.value}
      onIonChange={(event) => field.handleChange(event.detail.value)}
      onIonBlur={field.handleBlur}
      disabled={field.state.meta.isValidating || field.form.state.isSubmitting}
      multiple={multiple}
      {...props}
      className={cn(
        withAsterisk && "ion-required",
        field.state.meta.isTouched && "ion-touched",
        !!field.state.meta.errors && field.state.meta.errors.length > 0 && "ion-invalid",
        props.className,
      )}
    >
      {options.map((option) => (
        <IonSelectOption key={option.value} value={option.value}>
          {option.label}
        </IonSelectOption>
      ))}
    </IonSelect>
  );
});
